import { type CoreBundleItem,  type CoreProduct } from '@flecto/types'
type CalculatedAmounts = {
  totalWithTaxes: { cents: number }
  totalWithoutTaxes: { cents: number }
  taxes: number
}

export function getCalculatedAmounts(
  priceCents: number,
  taxRate: string,
  isTaxable: boolean
): CalculatedAmounts {
  const numericRate = Number(taxRate)
  const percentage = isTaxable && !Number.isNaN(numericRate) ? numericRate / 10 : 0

  const tax = Math.round(priceCents * (percentage / 100))
  const totalWithTaxes = priceCents + tax

  return {
    totalWithTaxes: { cents: totalWithTaxes },
    totalWithoutTaxes: { cents: priceCents },
    taxes: tax,
  }
}

export function getBundlePrice(
  product: CoreProduct,
  withTax: boolean,
 
): number {
  if (!product) return 0

  if (!product.bundle || !product.bundle_attributes) {
    const rule = product.pricing_rules?.[0]
    if (!rule) return 0

    const amounts = getCalculatedAmounts(
      rule.price_cents ?? 0,
      product?.tax_rate ?? 0,
      product?.taxable ?? false
    )

    return withTax ? amounts.totalWithTaxes.cents : amounts.totalWithoutTaxes.cents
  }

  const {
    apply_general_discount_to_bundle,
    apply_general_discount_amount = 0,
    apply_general_discount_type,
    bundle_items = [],
  } = product.bundle_attributes

  const getItemPrice = (item: CoreBundleItem) => {
    const rule = item.item.pricing_rules?.[0]
    if (!rule) return 0

    const amounts = getCalculatedAmounts(
      rule.price_cents ?? 0,
      item.item.tax_rate,
      item.item.taxable
    )

    return withTax ? amounts.totalWithTaxes.cents : amounts.totalWithoutTaxes.cents
  }

  const base = bundle_items.reduce((sum, item) => {
    const quantity = item.quantity ?? 1
    return sum + getItemPrice(item) * quantity
  }, 0)

  if (apply_general_discount_to_bundle) {
    return apply_general_discount_type === 'discount_percentage'
      ? Math.round(base * (1 - apply_general_discount_amount / 100))
      : Math.max(base - apply_general_discount_amount, 0)
  }

  const totalWithDiscounts = bundle_items.reduce((sum, item) => {
    const quantity = item.quantity ?? 1
    const price = getItemPrice(item)
    const discount = item.discount_amount ?? 0
    const discounted = price * (1 - discount / 100)
    return sum + discounted * quantity
  }, 0)

  return Math.round(totalWithDiscounts)
}
